/**
 * Peer Facade — Strangler Fig Pattern
 *
 * All consumer modules (inputEvents.ts, peerMessage.ts, connectionEvents.ts,
 * player.ts) call these facade functions. The flag USE_NEW_PEER in config.json
 * determines which implementation is used.
 *
 * USE_NEW_PEER=false (default): legacy @playcastdotio/Peer library.
 * USE_NEW_PEER=true:  new ClientPeerManager with @bowbee/peer-lite.
 */

import { createQualityManager } from '@playcastdotio/Peer';
import { generators, PeerIdType, PeerMessage, PeerMessageType, websocket } from '@playcastdotio/Messaging';
import { sendPlayerFrameratePanickedEvent } from '../analytics/playerConnectionAnalytics';
import { isAppleDevice, isMobileDevice } from '../../utility/utility';
import * as realtime from './realtimeMessaging';
import { getPersistentPlayerId } from '..';
import { handleHostMessage } from './hostMessaging/handleMessages';
import { invitationState, playerNameState } from '../stores/lobbyStores';
import { authStore, joinState } from '../stores/globalStores';
import { playerConnectionState, mediaStreamState } from '../stores/playerStores';
import config from '../../config.json';
import { ClientPeerManager, RetryPolicy } from './ClientPeerManager';
import { PeerLiteConnectionFactory } from '@playcastdotio/PeerCore';
import { getPreFetchedIceServers, type IceServerCredentials } from './iceServerPrefetch';
import {
  createPeerLegacy,
  getLegacyPeer,
  closeLegacyPeer,
  sendLegacyPeerMessage,
  handleLegacyPeerMessage,
  devHardResetVideoLegacy,
} from './peerLegacy';

// ========================================
// Shared utilities
// ========================================

export const recordDisconnectReason = (reason: string, context?: string) => {
  try {
    if (typeof window === 'undefined' || !('localStorage' in window)) return;
    const key = 'playcast.peer.disconnectHistory';
    const now = new Date().toLocaleString();
    const raw = window.localStorage.getItem(key);
    const history: Array<{ at: string; reason: string; context?: string }> = raw ? JSON.parse(raw) : [];
    history.unshift({ at: now, reason, context });
    window.localStorage.setItem(key, JSON.stringify(history.slice(0, 20)));
  } catch (err) {
    console.warn('[Marketing] Failed to record disconnect reason', err);
  }
};

export const qualityManager = createQualityManager();

// ========================================
// Legacy quality manager wiring (only when USE_NEW_PEER=false)
// ========================================

if (!config.USE_NEW_PEER) {
  const PANIC_COUNT_THRESHOLD = 3;
  const PANIC_HARD_RESET_TIMEOUT = 4000;
  let frameratePanicCount = 0;
  let frameratePanicTimeout: NodeJS.Timeout | undefined;

  qualityManager.onFrameratePanic((reason) => {
    console.error('Framerate Panic!');
    const peer = getLegacyPeer();
    if (peer) {
      let hasTracks = false;
      for (const trackId in peer.incomingTracks.currentState) {
        const track = peer.incomingTracks.currentState[trackId];
        if (track.metadata.type === 'video') { hasTracks = true; break; }
      }
      if (!hasTracks) { frameratePanicCount = 0; return; }
      frameratePanicCount++;
      const hardReset = frameratePanicCount > PANIC_COUNT_THRESHOLD;
      const panicMessage = generators.peer.frameratePanic('player', peer.peerId, hardReset);
      peer.sendMessage(panicMessage);
      sendPlayerFrameratePanickedEvent(hardReset, reason);
      if (frameratePanicTimeout) clearTimeout(frameratePanicTimeout);
      frameratePanicTimeout = setTimeout(() => { frameratePanicCount = 0; }, PANIC_HARD_RESET_TIMEOUT);
    }
  });

  qualityManager.preferredQualityState.addListener((state) => {
    const peer = getLegacyPeer();
    if (peer) {
      console.log('Changing quality to', state.profile);
      peer.changeQualityProfile(state.profile);
    }
  });
}

// ========================================
// NEW PATH — ClientPeerManager
// ========================================

let clientPeerManager: ClientPeerManager | undefined;

// Mutable signaling targets -- set before connect(), read by callbacks
let signalingPlayerId = '';
let signalingHostBroadcastId = '';

// ========================================
// Peer Milestone Callbacks (for analytics)
// ========================================

export type PeerMilestoneCallbacks = {
  onPeerIdAcquired?: (peerId: string) => void;
  onSdpOfferSent?: () => void;
  onSdpAnswerReceived?: () => void;
  onIceCandidatesSent?: (candidateCount: number) => void;
  onIceChecking?: () => void;
  onIceConnected?: () => void;
  onRemoteStreamReceived?: () => void;
  onIceFailed?: () => void;
};

let milestoneCallbacks: PeerMilestoneCallbacks = {};
let iceCandidatesSentFired = false;

export function setPeerMilestoneCallbacks(callbacks: PeerMilestoneCallbacks): void {
  milestoneCallbacks = callbacks;
  iceCandidatesSentFired = false;
}

const quickstartRetryPolicy: RetryPolicy = {
  maxRetries: 2,
  shouldRetry: (reason: string, _attempt: number) => {
    const r = reason.toLowerCase();
    if (r.includes('quickstart:') || r.includes('player:close') || r.includes('ad_mode:')) return false;
    if (r.includes('kicked') || r.includes('refused') || r.includes('invalid')) return false;
    if (r.startsWith('host:')) return false;
    return true;
  },
  retryDelayMs: (attempt: number) => Math.min(1000 * Math.pow(2, attempt), 5000),
};

const peerCallbacks = {
  onConnected: () => {
    playerConnectionState.updateState({ ...playerConnectionState.currentState, status: 'connected' });
  },
  onDisconnected: (reason: string) => {
    recordDisconnectReason(reason, 'marketing-demo:new-peer');
    playerConnectionState.updateState({ ...playerConnectionState.currentState, status: 'disconnected', kickReason: reason });
  },
  onRemoteStream: (stream: MediaStream) => {
    milestoneCallbacks.onRemoteStreamReceived?.();
    handleRemoteStreamNew(stream);
  },
  onDataMessage: (data: string | ArrayBuffer) => {
    if (typeof data === 'string') {
      try { handleHostMessage(JSON.parse(data)); } catch { console.warn('[ClientPeerManager] Invalid JSON data channel message'); }
    }
  },
  onSignalGenerated: (sdp: RTCSessionDescriptionInit) => {
    if (!sdp.sdp) {
      throw new Error('SDP is undefined');
    }
    if (sdp.type === 'offer') {
      milestoneCallbacks.onSdpOfferSent?.();
    }
    const msgType = sdp.type === 'offer' ? PeerMessageType.PEER_SDP_OFFER : PeerMessageType.PEER_SDP_ANSWER;
    realtime.sendMessage(websocket.generators.peer(msgType, { fromId: signalingPlayerId, toId: signalingHostBroadcastId, sdp: { type: sdp.type as 'offer' | 'answer', sdp: sdp.sdp ?? '' } }));
  },
  onIceCandidatesGenerated: (candidates: RTCIceCandidate[]) => {
    if (!iceCandidatesSentFired) {
      iceCandidatesSentFired = true;
      milestoneCallbacks.onIceCandidatesSent?.(candidates.length);
    }
    const json = candidates.map((c) => JSON.stringify(c));
    realtime.sendMessage(websocket.generators.peer(PeerMessageType.PEER_ICE_CANDIDATE, { fromId: signalingPlayerId, toId: signalingHostBroadcastId, candidates: { json } }));
  },
  onRetrying: (attempt: number, reason: string) => console.log(`[ClientPeerManager] Retrying (attempt ${attempt}): ${reason}`),
  onIceChecking: () => {
    milestoneCallbacks.onIceChecking?.();
  },
  onIceConnected: () => {
    milestoneCallbacks.onIceConnected?.();
  },
  onIceFailed: () => {
    milestoneCallbacks.onIceFailed?.();
  },
};

function ensureClientPeerManager(): ClientPeerManager {
  if (!clientPeerManager) {
    clientPeerManager = new ClientPeerManager(
      new PeerLiteConnectionFactory(),
      peerCallbacks,
      quickstartRetryPolicy,
    );
  }
  return clientPeerManager;
}

/**
 * Pre-warm the RTCPeerConnection with TURN credentials so ICE candidate
 * gathering starts immediately, well before a match is found.
 *
 * Called automatically by iceServerPrefetch.ts the instant credentials arrive.
 * Accepts optional pre-resolved credentials to avoid re-awaiting the promise.
 */
export async function preWarmPeerConnection(credentials?: IceServerCredentials | null): Promise<void> {
  if (!config.USE_NEW_PEER) return;

  const iceServers = credentials ?? await getPreFetchedIceServers();
  if (!iceServers) {
    console.log('[peer] No prefetched ICE servers available, skipping pre-warm');
    return;
  }

  const manager = ensureClientPeerManager();
  if (manager.hasPreWarmedConnection()) return;

  manager.preWarm([iceServers as unknown as RTCIceServer]);
  console.log('[peer] Pre-warmed RTCPeerConnection with prefetched TURN credentials');
}

async function connectClientPeerManager(playerId: string, hostBroadcastId: string): Promise<void> {
  signalingPlayerId = playerId;
  signalingHostBroadcastId = hostBroadcastId;

  const manager = ensureClientPeerManager();
  const stateIceServers = playerConnectionState.currentState.iceServers;
  const prefetchedIceServers = !stateIceServers ? await getPreFetchedIceServers() : null;
  const iceServers = stateIceServers ?? prefetchedIceServers;

  const metadata = {
    displayName: playerNameState.currentState.name,
    playerPersistentId: getPersistentPlayerId(),
    email: authStore.currentState.email,
    playerAuthId: authStore.currentState.playerAuthId,
    invitationPlayerId: invitationState.currentState.invitationPlayerId || authStore.currentState.playerAuthId || getPersistentPlayerId(),
    isApple: isAppleDevice(),
    isMobile: isMobileDevice(),
  };

  if (!iceServers) {
    console.warn('[peer] No ICE servers available from state or prefetch — TURN relay will be unavailable');
  }

  manager.connect(hostBroadcastId, iceServers ? [iceServers as unknown as RTCIceServer] : [], metadata);
}

function createPeerNew(hostBroadcastId: string): void {
  if (clientPeerManager?.isConnected()) {
    clientPeerManager.close('player:close:duplicate_peer');
  }

  // If fast-start already pre-created a peer ID, skip the PEER_GET_ID round-trip
  const existingPeerId = playerConnectionState.currentState.playerPeerId;
  if (existingPeerId) {
    console.log('[peer] Using pre-created peer ID from fast-start:', existingPeerId);
    milestoneCallbacks.onPeerIdAcquired?.(existingPeerId);
    playerConnectionState.updateState({
      ...playerConnectionState.currentState,
      status: 'connecting',
    });
    connectClientPeerManager(existingPeerId, hostBroadcastId);
    return;
  }

  playerConnectionState.updateState({
    ...playerConnectionState.currentState,
    status: 'connecting',
    playerPeerId: '',
  });

  realtime.sendMessage(
    websocket.generators.peer(PeerMessageType.PEER_GET_ID, {
      peerIdType: PeerIdType.PLAYER,
      peerMetadata: {
        isMobile: isMobileDevice(),
        isApple: isAppleDevice(),
        displayName: playerNameState.currentState.name,
      },
    }),
  );

  const waitForPeerId = playerConnectionState.addListener((state) => {
    if (state.playerPeerId === '') return;
    playerConnectionState.removeListener(waitForPeerId);
    milestoneCallbacks.onPeerIdAcquired?.(state.playerPeerId);
    connectClientPeerManager(state.playerPeerId, hostBroadcastId);
  });
}

function handleRemoteStreamNew(stream: MediaStream): void {
  const newVideoStream = new MediaStream();
  const newAudioStream = new MediaStream();

  stream.getVideoTracks().forEach((t) => {
    newVideoStream.addTrack(t);
    monitorTrack(t, 'video');
  });
  stream.getAudioTracks().forEach((t) => {
    newVideoStream.addTrack(t);
    newAudioStream.addTrack(t);
    monitorTrack(t, 'audio');
  });

  stream.onremovetrack = (ev) => {
    console.error('[peer] 🚨 Remote stream track removed:', {
      kind: ev.track.kind,
      id: ev.track.id,
      readyState: ev.track.readyState,
      timestamp: new Date().toISOString(),
    });
  };

  mediaStreamState.updateState({ stream: newVideoStream, audioStream: newAudioStream });
}

function monitorTrack(track: MediaStreamTrack, kind: 'video' | 'audio'): void {
  track.onended = () => {
    console.error(`[peer] 🚨 Remote ${kind} track ENDED`, {
      id: track.id,
      label: track.label,
      readyState: track.readyState,
      muted: track.muted,
      timestamp: new Date().toISOString(),
    });
    if (kind === 'video') {
      window.dispatchEvent(new CustomEvent('playcast:video:track-ended', { detail: { trackId: track.id } }));
    }
  };

  track.onmute = () => {
    console.warn(`[peer] ⚠️ Remote ${kind} track MUTED`, {
      id: track.id,
      readyState: track.readyState,
      timestamp: new Date().toISOString(),
    });
  };

  track.onunmute = () => {
    console.log(`[peer] ✅ Remote ${kind} track UNMUTED`, {
      id: track.id,
      readyState: track.readyState,
      timestamp: new Date().toISOString(),
    });
  };
}

function handleIncomingPeerMessageNew(message: PeerMessage): void {
  if (!clientPeerManager) return;
  switch (message.type) {
    case PeerMessageType.PEER_SDP_OFFER:
      if ('sdp' in message && message.sdp) {
        clientPeerManager.handleRemoteSdp(message.sdp as RTCSessionDescriptionInit);
      }
      break;
    case PeerMessageType.PEER_SDP_ANSWER:
      if ('sdp' in message && message.sdp) {
        milestoneCallbacks.onSdpAnswerReceived?.();
        clientPeerManager.handleRemoteSdp(message.sdp as RTCSessionDescriptionInit);
      }
      break;
    case PeerMessageType.PEER_ICE_CANDIDATE:
      if ('candidates' in message && message.candidates) {
        const jsonCandidates = (message.candidates as { json: string[] }).json;
        for (const candidateJson of jsonCandidates) {
          try {
            const candidate = new RTCIceCandidate(JSON.parse(candidateJson));
            clientPeerManager.handleRemoteIceCandidate(candidate);
          } catch (e) {
            console.warn('[peer] Failed to parse ICE candidate:', e);
          }
        }
      }
      break;
  }
}

// ========================================
// FACADE — unified API for consumers
// ========================================

export const createPeer = async (hostBroadcastId: string) => {
  if (!hostBroadcastId) { console.error('No host broadcast id'); return; }
  if (config.USE_NEW_PEER) { createPeerNew(hostBroadcastId); return; }
  return createPeerLegacy(hostBroadcastId);
};

export const sendPeerMessage = (message: any): void => {
  if (config.USE_NEW_PEER) { clientPeerManager?.send(JSON.stringify(message)); return; }
  sendLegacyPeerMessage(message);
};

export const closePeer = (reason: string): void => {
  if (config.USE_NEW_PEER) { clientPeerManager?.close(reason); return; }
  closeLegacyPeer(reason);
};

export const handleIncomingPeerMessage = (message: PeerMessage): void => {
  if (config.USE_NEW_PEER) { handleIncomingPeerMessageNew(message); return; }
  handleLegacyPeerMessage(message);
};

export const getPlayerPeerId = (): string => {
  return playerConnectionState.currentState.playerPeerId || '';
};

export const playerDisconnect = () => {
  closePeer('player:close:manual_disconnect');
  joinState.updateState({ ...joinState.currentState, state: 'playerDisconnected' });
};

export const getPeer = () => getLegacyPeer();

export const devHardResetVideo = () => {
  if (!config.USE_NEW_PEER) devHardResetVideoLegacy();
};

export const getClientPeerManager = (): ClientPeerManager | undefined => clientPeerManager;
